import { useCallback } from 'react';
import { useChatStore } from '@/store/chatStore';
import { useVoiceSettingsStore } from '@/store/voiceSettingsStore';
import { aiService } from '@/services/aiService';
import { ttsService } from '@/services/ttsService';
import { ERROR_MESSAGES, APP_CONFIG } from '@/constants';
import { isValidMessage, sanitizeInput } from '@/utils/helpers';

export const useChat = () => {
  const {
    addMessage,
    updateMessage,
    setLoading,
    setSpeaking,
    setError,
    messages,
    isLoading,
    isSpeaking,
  } = useChatStore();
  const { selectedVoiceIndex, rate, pitch, volume } = useVoiceSettingsStore();

  const simulateTyping = (messageId: string, fullText: string, callback?: () => void) => {
    let currentIndex = 0;
    const typingSpeed = 20; // milliseconds per character

    const typeNextChar = () => {
      if (currentIndex <= fullText.length) {
        updateMessage(
          messageId,
          fullText.substring(0, currentIndex),
          currentIndex < fullText.length
        );
        currentIndex++;
        setTimeout(typeNextChar, typingSpeed);
      } else {
        updateMessage(messageId, fullText, false);
        callback?.();
      }
    };

    typeNextChar();
  };

  const sendMessage = useCallback(
    async (text: string, skipUserMessage = false) => {
      // Validate input
      if (!isValidMessage(text)) {
        setError(ERROR_MESSAGES.INVALID_INPUT);
        return;
      }

      const sanitized = sanitizeInput(text);

      if (sanitized.length > APP_CONFIG.MAX_MESSAGE_LENGTH) {
        setError(ERROR_MESSAGES.MESSAGE_TOO_LONG);
        return;
      }

      // Clear any previous errors
      setError(null);

      // Add user message only if not skipped (for edit functionality)
      if (!skipUserMessage) {
        addMessage(sanitized, 'user');
      }
      setLoading(true);

      try {
        // Get AI response
        const response = await aiService.getResponse(sanitized);

        if (response.error) {
          throw new Error(response.error);
        }

        // Add AI message with typing animation
        const messageId = addMessage('', 'ai', false, true);

        // Start speaking immediately as typing begins
        if (ttsService.isSupported()) {
          speakMessage(response.text);
        }

        simulateTyping(messageId, response.text);
      } catch (error) {
        console.error('Chat error:', error);
        let errorMessage: string;
        let toastError: string;

        if (error instanceof Error) {
          // Check for specific error types
          if (error.message.includes('retries')) {
            errorMessage =
              "I'm experiencing high traffic right now. Please try again in a moment! 🔄";
            toastError = 'Service temporarily busy. Please retry.';
          } else if (error.message.includes('Network Error') || error.message.includes('timeout')) {
            errorMessage =
              "Oops! I'm having trouble connecting. Please check your internet connection and try again. 🌐";
            toastError = 'Connection error. Check your network.';
          } else if (error.message.includes('429')) {
            errorMessage =
              "I'm getting a lot of requests right now! Please wait a moment before trying again. ⏳";
            toastError = 'Rate limit reached. Please wait.';
          } else if (error.message.includes('401') || error.message.includes('403')) {
            errorMessage =
              "I'm having authentication issues. The service may need to be reconfigured. 🔐";
            toastError = 'Authentication error. Contact support.';
          } else if (
            error.message.includes('500') ||
            error.message.includes('502') ||
            error.message.includes('503')
          ) {
            errorMessage =
              'The AI service is temporarily unavailable. Please try again in a few moments! 🛠️';
            toastError = 'Service temporarily down. Please retry.';
          } else {
            errorMessage =
              'Sorry, something unexpected happened! Please try sending your message again. 😅';
            toastError = 'An error occurred. Please try again.';
          }
        } else {
          errorMessage = "Hmm, I encountered an unexpected issue. Let's try that again! 🤔";
          toastError = ERROR_MESSAGES.GENERIC_ERROR;
        }

        addMessage(errorMessage, 'ai', true);
        setError(toastError);
      } finally {
        setLoading(false);
      }
    },
    [addMessage, updateMessage, setLoading, setError, setSpeaking]
  );

  const speakMessage = useCallback(
    (text: string) => {
      setSpeaking(true);

      // Get selected voice
      const voice =
        selectedVoiceIndex >= 0 ? ttsService.getVoiceByIndex(selectedVoiceIndex) : undefined;

      ttsService.speak(
        text,
        {
          voice,
          rate,
          pitch,
          volume,
        },
        () => {
          // onStart
          setSpeaking(true);
        },
        () => {
          // onEnd
          setSpeaking(false);
        },
        (error) => {
          // onError
          console.error('TTS error:', error);
          setSpeaking(false);
          setError(ERROR_MESSAGES.TTS_ERROR);
        }
      );
    },
    [setSpeaking, setError, selectedVoiceIndex, rate, pitch, volume]
  );

  const stopSpeaking = useCallback(() => {
    ttsService.stop();
    setSpeaking(false);
  }, [setSpeaking]);

  return {
    messages,
    isLoading,
    isSpeaking,
    sendMessage,
    stopSpeaking,
  };
};
